import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GetGoogleSignInStatusAction, googleSignInUrl } from "../../store/action";
import { TERMS_VERSION } from "../../constants/legal";
import { termsContent, privacyContent } from "../legal/legalContent";
import LegalModal from "../legal/LegalModal";
import { isInAppBrowser } from "../../utils/inAppBrowser";
import { textLinkClass } from "../ui/formStyles";

/**
 * The refusals the API's Google callback can send back, as `#google_error=…`
 * on the page the person started from. Anything else reads as "failed".
 */
export const GOOGLE_ERROR_CODES = [
  "cancelled",
  "failed",
  "expired",
  "unavailable",
  "unverified",
  "password_only",
  "linked_elsewhere",
  "terms_updated",
];

/**
 * Google's own "G", as its branding guidelines require it: four colours, never
 * recoloured, never on its own without the words beside it.
 */
export const GoogleMark = ({ className = "size-[18px] shrink-0" }) => (
  <svg
    viewBox="0 0 48 48"
    className={className}
    aria-hidden="true"
    focusable="false"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

GoogleMark.propTypes = {
  className: PropTypes.string,
};

// Google's mandated light-theme button: white fill, #747775 stroke, #1F1F1F
// text. Hex rather than our tokens on purpose — these are Google's colours,
// not ours, and its guidelines do not leave them to the site. Pill-shaped and
// 44px tall to sit beside primaryButtonClass and meet the touch-target bar.
const googleButtonClass =
  "google-signin-button inline-flex min-h-11 w-full items-center justify-center gap-3 rounded-full border border-[#747775] bg-white px-6 py-2.5 text-sm font-semibold text-[#1f1f1f] transition hover:bg-[#f8faff] hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

const Divider = ({ label }) => (
  <div className="google-signin-divider my-6 flex items-center gap-3 text-xs font-medium text-ink-400">
    <span className="h-px min-w-6 flex-1 bg-ink-200" aria-hidden="true" />
    <span className="min-w-0 text-center">{label}</span>
    <span className="h-px min-w-6 flex-1 bg-ink-200" aria-hidden="true" />
  </div>
);

Divider.propTypes = {
  label: PropTypes.string.isRequired,
};

/** The code the callback put in the address, if it is one we know. */
const readGoogleError = (hash) => {
  const code = new URLSearchParams((hash || "").replace(/^#/, "")).get("google_error");
  if (!code) return null;
  return GOOGLE_ERROR_CODES.includes(code) ? code : "failed";
};

/**
 * Shown instead of the button inside Facebook's, Instagram's and other apps'
 * built-in browsers, where Google refuses to sign anyone in. Says how to get
 * to a real browser, and offers the link to paste there.
 */
const InAppBrowserHint = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch (error) {
      // No clipboard in this webview. The instructions above still work.
    }
  };

  return (
    <div className="google-inapp-hint rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-fluid-sm text-ink-700">
      <p className="flex items-center gap-2 font-semibold text-ink-900">
        <GoogleMark />
        <span className="min-w-0">{t("GoogleSignIn.inApp.title")}</span>
      </p>
      <p className="mt-1 leading-relaxed">{t("GoogleSignIn.inApp.body")}</p>
      <button
        type="button"
        onClick={copyLink}
        className={`${textLinkClass} mt-1 inline-flex min-h-11 items-center gap-1.5 text-sm`}
      >
        <i className={`bi ${copied ? "bi-check2" : "bi-link-45deg"}`} aria-hidden="true" />
        {copied ? t("GoogleSignIn.inApp.copied") : t("GoogleSignIn.inApp.copy")}
      </button>
    </div>
  );
};

/**
 * "Continue with Google", for the login and signup pages.
 *
 * Renders nothing until the API says the feature is configured — no button
 * that leads to an error page at Google. A plain link, not a script: the API
 * runs the whole OAuth flow (GoogleAuthController), so nothing from Google
 * loads on our page.
 *
 * Also the place a refused attempt comes back to: the API returns the browser
 * to this page with `#google_error=…`, which is shown here and then taken out
 * of the address.
 *
 * Agreement to the terms is the sign-in-wrap line under the button — pressing
 * it is the act. The API records it, against the version shown here, only if
 * it creates an account.
 *
 * e2e: the Playwright specs run without an API, so the status request fails
 * and nothing here renders — the login and signup contracts are untouched.
 */
const GoogleSignIn = ({
  mode,
  rememberMe = false,
  role = null,
  inviteToken = null,
  divider = "before",
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(null);
  const [error, setError] = useState(() => readGoogleError(location.hash));
  const [inApp] = useState(() => isInAppBrowser());
  const [openLegalDoc, setOpenLegalDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    GetGoogleSignInStatusAction()
      .then((value) => {
        if (!cancelled) setEnabled(value);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Read once, then out of the address: a reload or a shared link must not
  // show the same refusal again.
  useEffect(() => {
    if (readGoogleError(location.hash)) {
      setError(readGoogleError(location.hash));
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [location.hash]);

  const showButton = enabled === true;
  if (!showButton && !error) return null;

  const href = googleSignInUrl({
    mode,
    role: mode === "signup" ? role : null,
    inviteToken: mode === "signup" ? inviteToken : null,
    termsVersion: TERMS_VERSION,
    rememberMe: mode === "login" ? rememberMe : false,
  });

  return (
    <div className="google-signin">
      {error && (
        <div
          role="alert"
          className="google-signin-error mb-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-fluid-sm leading-relaxed text-ink-700"
        >
          <i className="bi bi-exclamation-triangle mt-0.5 text-amber-600" aria-hidden="true" />
          <span className="min-w-0">{t(`GoogleSignIn.errors.${error}`)}</span>
        </div>
      )}

      {showButton && (
        <>
          {divider === "before" && <Divider label={t("GoogleSignIn.or")} />}

          {inApp ? (
            <InAppBrowserHint />
          ) : (
            <>
              {/* nofollow: it leads to the API and then to Google's sign-in,
                  nothing a crawler should spend a visit on (robots.txt also
                  keeps it out of /api/Auth/). */}
              <a href={href} rel="nofollow" className={googleButtonClass}>
                <GoogleMark />
                <span className="min-w-0">{t("GoogleSignIn.continue")}</span>
              </a>

              <p className="google-signin-consent mt-3 text-center text-xs leading-relaxed text-ink-500">
                {t("GoogleSignIn.consent.prefix")}{" "}
                <button
                  type="button"
                  onClick={() => setOpenLegalDoc("terms")}
                  className={`${textLinkClass} cursor-pointer`}
                >
                  {t("GoogleSignIn.consent.termsLink")}
                </button>{" "}
                {t("GoogleSignIn.consent.connector")}{" "}
                <button
                  type="button"
                  onClick={() => setOpenLegalDoc("privacy")}
                  className={`${textLinkClass} cursor-pointer`}
                >
                  {t("GoogleSignIn.consent.privacyLink")}
                </button>
                .
              </p>

              <LegalModal
                content={termsContent}
                open={openLegalDoc === "terms"}
                onClose={() => setOpenLegalDoc(null)}
                showVersion
              />
              <LegalModal
                content={privacyContent}
                open={openLegalDoc === "privacy"}
                onClose={() => setOpenLegalDoc(null)}
              />
            </>
          )}

          {divider === "after" && (
            <Divider
              label={t(mode === "signup" ? "GoogleSignIn.orEmail" : "GoogleSignIn.orEmailLogin")}
            />
          )}
        </>
      )}
    </div>
  );
};

GoogleSignIn.propTypes = {
  /** The page the button is on: decides what a newcomer's account becomes. */
  mode: PropTypes.oneOf(["login", "signup"]).isRequired,
  /** "Keep me signed in", from the login form. */
  rememberMe: PropTypes.bool,
  /** The account type picked on the signup page. */
  role: PropTypes.number,
  /** A company invitation that checked out, from /signup?invite=. */
  inviteToken: PropTypes.string,
  /** Which side of the button the "or" rule goes. */
  divider: PropTypes.oneOf(["before", "after"]),
};

export default GoogleSignIn;
